import { useState, useMemo } from 'react';
import { Users, AlertCircle } from 'lucide-react';
import type { Participant } from '../types';
import { calculateAgreement } from '../utils/aggregation';

interface ComparisonViewProps {
  participants: Participant[];
  items: Array<{ id: string; name: string }>;
}

export function ComparisonView({ participants, items }: ComparisonViewProps) {
  const [selectedParticipant1, setSelectedParticipant1] = useState<string | null>(
    participants[0]?.id || null
  );
  const [selectedParticipant2, setSelectedParticipant2] = useState<string | null>(
    participants.length > 1 ? participants[1]?.id : null
  );

  // Calculate agreement between selected participants
  const agreement = useMemo(() => {
    if (!selectedParticipant1 || !selectedParticipant2) return null;
    const p1 = participants.find((p) => p.id === selectedParticipant1);
    const p2 = participants.find((p) => p.id === selectedParticipant2);
    if (!p1 || !p2) return null;
    return calculateAgreement(p1, p2);
  }, [selectedParticipant1, selectedParticipant2, participants]);

  // Find differences between two participants
  const differences = useMemo(() => {
    if (!selectedParticipant1 || !selectedParticipant2) return [];
    const p1 = participants.find((p) => p.id === selectedParticipant1);
    const p2 = participants.find((p) => p.id === selectedParticipant2);
    if (!p1 || !p2) return [];

    const differences: Array<{
      itemId: string;
      itemName: string;
      rank1: number;
      rank2: number;
      difference: number;
    }> = [];

    items.forEach((item) => {
      const rank1 = p1.ranking.indexOf(item.id);
      const rank2 = p2.ranking.indexOf(item.id);
      
      if (rank1 !== -1 || rank2 !== -1) {
        const r1 = rank1 === -1 ? items.length + 1 : rank1 + 1;
        const r2 = rank2 === -1 ? items.length + 1 : rank2 + 1;
        const diff = Math.abs(r1 - r2);
        
        if (diff > 0) {
          differences.push({
            itemId: item.id,
            itemName: item.name,
            rank1: r1,
            rank2: r2,
            difference: diff,
          });
        }
      }
    });

    return differences.sort((a, b) => b.difference - a.difference);
  }, [selectedParticipant1, selectedParticipant2, participants, items]);

  const getAgreementColor = (score: number) => {
    if (score >= 0.8) return 'text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] border-[var(--green)]';
    if (score >= 0.6) return 'text-[var(--text-sec)] bg-[var(--slate-dim)] border-[var(--slate)]';
    if (score >= 0.4) return 'text-[var(--mustard)] bg-[var(--mustard-dim)] border-[var(--mustard)]';
    return 'text-[var(--coral)] bg-[var(--coral-dim)] border-[var(--coral)]';
  };

  const getDifferenceColor = (diff: number, maxDiff: number) => {
    const ratio = diff / maxDiff;
    if (ratio >= 0.7) return 'bg-[var(--coral-dim)] border-[var(--coral)]';
    if (ratio >= 0.4) return 'bg-[var(--mustard-dim)] border-[var(--mustard)]';
    return 'bg-[var(--slate-dim)] border-[var(--slate)]';
  };

  const maxDifference = differences.length > 0 
    ? Math.max(...differences.map((d) => d.difference))
    : 1;

  return (
    <div className="space-y-6">
      {/* Participant Selection */}
      <div className="bg-[var(--bg-raised)] p-4 border border-[var(--rule)]">
        <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
          <Users className="h-5 w-5" />
          Compare Participants
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
              Participant 1
            </label>
            <select
              value={selectedParticipant1 || ''}
              onChange={(e) => setSelectedParticipant1(e.target.value)}
              className="w-full p-2 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500 focus:border-[var(--slate)]"
            >
              {participants.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
              Participant 2
            </label>
            <select
              value={selectedParticipant2 || ''}
              onChange={(e) => setSelectedParticipant2(e.target.value)}
              className="w-full p-2 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500 focus:border-[var(--slate)]"
            >
              {participants.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Agreement Score */}
      {agreement !== null && (
        <div
          className={`p-6  border ${getAgreementColor(agreement)}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold mb-1">Agreement Score</h4>
              <p className="text-sm opacity-80">
                How similar are these rankings?
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">
                {(agreement * 100).toFixed(0)}%
              </div>
              <div className="text-sm opacity-80">
                {agreement >= 0.8
                  ? 'Strong Agreement'
                  : agreement >= 0.6
                  ? 'Moderate Agreement'
                  : agreement >= 0.4
                  ? 'Weak Agreement'
                  : 'Low Agreement'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Differences */}
      {differences.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Key Differences
          </h3>
          <div className="space-y-2">
            {differences.map((diff) => {
              const p1 = participants.find((p) => p.id === selectedParticipant1);
              const p2 = participants.find((p) => p.id === selectedParticipant2);
              
              return (
                <div
                  key={diff.itemId}
                  className={`p-4 border  ${getDifferenceColor(
                    diff.difference,
                    maxDifference
                  )}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-semibold text-[var(--text-pri)]">
                        {diff.itemName}
                      </div>
                      <div className="text-sm text-[var(--text-sec)] mt-1">
                        <span className="font-medium">
                          {p1?.name}: Rank {diff.rank1}
                        </span>
                        {' • '}
                        <span className="font-medium">
                          {p2?.name}: Rank {diff.rank2}
                        </span>
                        {' • '}
                        <span className="font-medium text-[var(--coral)]">
                          Difference: {diff.difference}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-[var(--coral)]">
                        {diff.difference}
                      </div>
                      <div className="text-xs text-[var(--text-ter)]">positions</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {differences.length === 0 && agreement !== null && agreement > 0.9 && (
        <div className="p-6 bg-[var(--green-dim)] border border-[var(--green)] text-center">
          <div className="text-[var(--green)] dark:text-[var(--text-ter)] font-semibold">
            These participants have very similar rankings!
          </div>
          <div className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] mt-1">
            No significant differences found.
          </div>
        </div>
      )}
    </div>
  );
}


